import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom"; // Using Link from react-router-dom
import { auth, db } from "./firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import GoogleSignInButton from "./GoogleSignInButton";
import { Eye, EyeOff, ShieldCheck, ChevronLeft } from "lucide-react";

const LoginForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isForgotHovered, setIsForgotHovered] = useState(false);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [countries, setCountries] = useState([]);
  const [additionalData, setAdditionalData] = useState({ country: null, domainName: "" });
  
  // State for initial page loading spinner
const [isPageReady, setIsPageReady] = useState(false);
const [showSpinner, setShowSpinner] = useState(false);  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // --- Theme Colors ---
  const primaryOrange = "#FF6B00";
  const darkOrange = "#E66000";
  const borderColor = "#E5E7EB";

  // --- Page Load & Resize Logic ---
 useEffect(() => {
  const handleResize = () => setWindowWidth(window.innerWidth);
  window.addEventListener("resize", handleResize);

  // spinner delay
  const spinnerTimer = setTimeout(() => {
    setShowSpinner(true);
  }, 400); // only show spinner if load takes longer than 400ms

  // simulate loading assets
  const loadPage = async () => {
    try {
      // example: fetch something or wait for assets
      await new Promise((resolve) => setTimeout(resolve)); 
    } finally {
      clearTimeout(spinnerTimer);
      setIsPageReady(true);
    }
  };

  loadPage();

  return () => {
    window.removeEventListener("resize", handleResize);
    clearTimeout(spinnerTimer);
  };
}, []);

  const isMobile = windowWidth <= 700;
  const isTablet = windowWidth > 700 && windowWidth <= 1024;
  const isSmallScreen = windowWidth <= 1024;

  // --- Fetch Countries ---
  useEffect(() => {
    fetch("https://restcountries.com/v3.1/all?fields=name,flags")
      .then((res) => res.json())
      .then((data) => {
        const formatted = data.map((c) => ({
          value: c.name.common,
          label: (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img src={c.flags.svg} alt="flag" style={{ width: "20px", borderRadius: "2px" }} />
              {c.name.common}
            </div>
          ),
        })).sort((a, b) => a.value.localeCompare(b.value));
        setCountries(formatted);
      })
      .catch(() => toast.error("Failed to load countries"));
  }, []);

  // --- Authentication Handlers ---
  const handleNewGoogleUser = (user) => {
    setTempUser(user);
    setShowDetailsPopup(true);
  };

 const handleLogin = async (e) => {
  e.preventDefault();
  setLoading(true);
  const toastId = toast.loading("Verifying Account..");
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const docSnap = await getDoc(doc(db, "users", userCredential.user.uid));
    
    if (docSnap.exists()) {
      const userData = docSnap.data();
      toast.success("Logged in!", { id: toastId });

      // Check for admin or superadmin roles
      if (userData.role === "admin" || userData.role === "superadmin") {
        navigate("/dashboard");
      } else {
        navigate("/userDashboard");
      }
    }
  } catch (err) {
    toast.error("Invalid credentials", { id: toastId });
  }
  setLoading(false);
};

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    if (!additionalData.country || !additionalData.domainName) {
      toast.error("Please select your country and domain.");
      return;
    }
    const saveToast = toast.loading("Creating your profile...");
    try {
      await setDoc(doc(db, "users", tempUser.uid), {
        name: tempUser.displayName,
        email: tempUser.email,
        country: additionalData.country.value,
        domainName: additionalData.domainName,
        role: "user",
        createdAt: new Date()
      });
      toast.success("All set! Welcome.", { id: saveToast });
      setShowDetailsPopup(false);
      navigate("/userDashboard");
    } catch (err) {
      toast.error("Error saving data.", { id: saveToast });
    }
  };

  const handleForgotPassword = async () => {
    if (!email) return toast.error("Enter email first");
    await sendPasswordResetEmail(auth, email);
    toast.success("Reset link sent!");
  };

  // --- STYLES ---

  const pageWrapperStyle = {
    display: "flex",
    flexDirection: isSmallScreen ? "column" : "row",
    minHeight: "100vh",
    width: "100%",
    fontFamily: "'Inter', sans-serif",
    backgroundColor: isSmallScreen ? "#F3F4F6" : "white",
    overflowX: "hidden"
  };

  const loaderContainerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    width: "100%",
    backgroundColor: "#F3F4F6",
  };

  const spinnerStyle = {
    width: "50px",
    height: "50px",
    border: `5px solid rgba(255, 107, 0, 0.1)`,
    borderTop: `5px solid ${primaryOrange}`,
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  };

  const leftSideStyle = {
    display: isSmallScreen ? "none" : "flex",
    flex: "1.4",
    position: "relative",
    backgroundImage: `url('https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    flexDirection: "column",
    justifyContent: "center",
    padding: "80px",
    color: "white",
  };

  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(135deg, rgba(255, 107, 0, 0.92) 0%, rgba(15, 15, 15, 0.85) 100%)",
    zIndex: 1
  };

  const backHomeButtonStyle = {
    position: "absolute",
    top: "40px",
    left: "40px",
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "white",
    fontSize: "14px",
    fontWeight: "600",
    padding: "10px 20px",
    borderRadius: "30px",
    backgroundColor: isBackHovered ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    textDecoration: "none"
  };

  const trustBadgeStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: "13px",
    width: "fit-content",
    marginBottom: "20px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(4px)"
  };

  const rightSideStyle = {
    flex: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backgroundColor: isSmallScreen ? "#F3F4F6" : "white",
  };

  const cardStyle = {
    width: "100%",
    maxWidth: isTablet ? "550px" : (isMobile ? "450px" : "400px"),
    backgroundColor: isSmallScreen ? "white" : "transparent",
    padding: isSmallScreen ? (isMobile ? "35px 25px" : "50px") : "0px",
    borderRadius: isSmallScreen ? "24px" : "0px",
    boxShadow: isSmallScreen ? "0 25px 50px -12px rgba(0, 0, 0, 0.08)" : "none",
    border: isSmallScreen ? "1px solid #E5E7EB" : "none",
    transition: "all 0.3s ease"
  };

  const getInputStyle = (id) => ({
    width: "100%",
    padding: "15px",
    margin: "7px 0",
    border: "none",
    borderBottom: `2px solid ${focusedInput === id ? primaryOrange : borderColor}`,
    outline: "none",
    fontSize: "15px",
    boxSizing: "border-box",
    transition: "all 0.3s ease",
    backgroundColor: focusedInput === id ? "rgba(255, 107, 0, 0.02)" : "transparent"
  });

  // --- Initial Loading Screen ---
if (!isPageReady && showSpinner) {
    return (
      <div style={loaderContainerStyle}>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <div style={spinnerStyle}></div>
      </div>
    );
  }

  return (
    <div style={pageWrapperStyle}>
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            textAlign: 'center',
            fontWeight: '500',
            fontSize: '14px',
            borderRadius: '10px',
          }
        }}
      />

      {!isSmallScreen && (
        <div style={leftSideStyle}>
          <div style={overlayStyle}></div>

          <Link
            to="/"
            style={backHomeButtonStyle}
            onMouseEnter={() => setIsBackHovered(true)}
            onMouseLeave={() => setIsBackHovered(false)}
          >
            <ChevronLeft size={18} /> Back to Home
          </Link>

          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={trustBadgeStyle}>
              <ShieldCheck size={18} color="#FFE0B2" />
              <span>Trusted by <strong style={{ color: "#FFE0B2" }}>10,000+</strong> Businesses Worldwide</span>
            </div>

            <h1 style={{ fontSize: "56px", fontWeight: "800", marginBottom: "24px", lineHeight: "1.1", letterSpacing: "-1.5px" }}>
              Grow Your <br />
              <span style={{ color: "#FFE0B2" }}>Business</span> Faster.
            </h1>
            <p style={{ fontSize: "19px", opacity: 0.9, lineHeight: "1.6", maxWidth: "500px", fontWeight: "300" }}>
              The all-in-one platform to manage your digital footprint. Connect with your audience and scale effortlessly.
            </p>

            <div style={{ marginTop: "50px", display: "flex", gap: "40px" }}>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "28px", fontWeight: "800", color: "#FFE0B2" }}>99.9%</div>
                <div style={{ fontSize: "11px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "1.5px", marginTop: "4px" }}>Uptime</div>
              </div>
              <div style={{ width: "1px", height: "45px", backgroundColor: "rgba(255,255,255,0.2)" }}></div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "28px", fontWeight: "800", color: "#FFE0B2" }}>24/7</div>
                <div style={{ fontSize: "11px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "1.5px", marginTop: "4px" }}>Support</div>
              </div>
              <div style={{ width: "1px", height: "45px", backgroundColor: "rgba(255,255,255,0.2)" }}></div>
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: "28px", fontWeight: "800", color: "#FFE0B2" }}>50+</div>
                <div style={{ fontSize: "11px", opacity: 0.7, textTransform: "uppercase", letterSpacing: "1.5px", marginTop: "4px" }}>Global Servers</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={rightSideStyle}>
        <div style={cardStyle}>
          {isSmallScreen && (
            <Link to="/" style={{ color: primaryOrange, marginBottom: "20px", display: "flex", alignItems: "center", fontSize: "14px", fontWeight: "600", cursor: "pointer", textDecoration: "none" }}>
              <ChevronLeft size={16} /> Home
            </Link>
          )}

          <div style={{ textAlign: isSmallScreen ? "left" : "center", marginBottom: "40px" }}>
            <h2 style={{
              margin: "0 0 3px 0", fontSize: isMobile ? "28px" : "36px", fontWeight: "900",
              background: `linear-gradient(to right, #111827, ${primaryOrange})`,
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", letterSpacing: "-1px",
              fontFamily: "'Poppins', sans-serif",
            }}>
              Welcome Back
            </h2>
            <p style={{ color: "#6B7280", fontSize: "15px" }}>Please enter your details to sign in.</p>
          </div>

          <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: "12px", fontWeight: "700", color: focusedInput === 'email' ? primaryOrange : "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>Email Address</label>
            <input
              type="email" placeholder="name@company.com" value={email}
              onFocus={() => setFocusedInput('email')} onBlur={() => setFocusedInput(null)}
              onChange={(e) => setEmail(e.target.value)} style={getInputStyle('email')} required
            />

            <div style={{ position: "relative", marginTop: "20px" }}>
              <label style={{ fontSize: "12px", fontWeight: "700", color: focusedInput === 'pass' ? primaryOrange : "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>Password</label>
              <input
                type={showPassword ? "text" : "password"} placeholder="••••••••" value={password}
                onFocus={() => setFocusedInput('pass')} onBlur={() => setFocusedInput(null)}
                onChange={(e) => setPassword(e.target.value)} style={{ ...getInputStyle('pass'), paddingRight: "45px" }} required
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: "absolute", right: "12px", top: "35px", cursor: "pointer", color: focusedInput === 'pass' ? primaryOrange : "#9CA3AF" }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </span>
            </div>

            <div style={{ textAlign: "right", marginBottom: "18px", marginTop: "8px" }}>
              <span
                onClick={handleForgotPassword}
                style={{ fontSize: "13px", color: isForgotHovered ? primaryOrange : "#6B7280", cursor: "pointer", fontWeight: "600" }}
                onMouseEnter={() => setIsForgotHovered(true)} onMouseLeave={() => setIsForgotHovered(false)}
              >
                Forgot Password?
              </span>
            </div>

            <button
              type="submit" disabled={loading}
              style={{
                width: "100%", padding: "18px", background: `linear-gradient(135deg, ${primaryOrange} 0%, ${darkOrange} 100%)`,
                color: "white", border: "none", borderRadius: "14px", cursor: loading ? "not-allowed" : "pointer", fontWeight: "700", fontSize: "16px",
                boxShadow: "0 10px 15px -3px rgba(255, 107, 0, 0.3)", transition: "all 0.2s ease",
                opacity: loading ? 0.8 : 1
              }}
            >
              {loading ? "Verifying Account..." : "SIGN IN"}
            </button>
          </form>

          <div style={{ display: "flex", alignItems: "center", margin: "30px 0", color: "#E5E7EB" }}>
            <div style={{ flex: 1, height: "1px", backgroundColor: borderColor }} />
            <span style={{ margin: "0 15px", fontSize: "11px", color: "#9CA3AF", fontWeight: "700", letterSpacing: "1px" }}>OR</span>
            <div style={{ flex: 1, height: "1px", backgroundColor: borderColor }} />
          </div>

          <GoogleSignInButton onNewUserFound={handleNewGoogleUser} />

          <div style={{ marginTop: "30px", fontSize: "14px", color: "#6B7280", textAlign: "center" }}>
            Don't have an account? <Link to="/register" style={{ color: primaryOrange, fontWeight: "700", textDecoration: "none" }}>Create account</Link>
          </div>
        </div>
      </div>

      {/* POPUP MODAL */}
      {showDetailsPopup && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, backdropFilter: "blur(10px)" }}>
          <div style={{ backgroundColor: "white", padding: "40px", borderRadius: "24px", width: "90%", maxWidth: "420px", textAlign: "center" }}>
            <h3 style={{ color: "#111827", fontSize: "24px", fontWeight: "800", marginBottom: "10px" }}>Complete Profile</h3>
            <form onSubmit={handleSaveDetails}>
              <div style={{ textAlign: "left", marginBottom: "20px" }}>
                <label style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "8px", display: "block" }}>Select Country</label>
                <Select options={countries} onChange={(opt) => setAdditionalData({ ...additionalData, country: opt })} />
              </div>
              <div style={{ textAlign: "left" }}>
                <label style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "5px", display: "block" }}>Business Domain</label>
                <input style={getInputStyle('domain')} placeholder="e.g. cloudscape.io" required value={additionalData.domainName} onChange={(e) => setAdditionalData({ ...additionalData, domainName: e.target.value.replace(/\s/g, "") })} />
              </div>
              <button type="submit" style={{ width: "100%", padding: "16px", marginTop: "30px", background: primaryOrange, color: "white", border: "none", borderRadius: "12px", fontWeight: "700", cursor: "pointer" }}>
                FINISH & EXPLORE
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;